import { roleLabel } from '../../lib/roles'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DataTable, ErrorBox, Field, InfoBox, KpiCard, Panel, ScreenHeading, Tag, WarnBox } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { dateTime } from '../../lib/format'
import type { Catalogue } from '../../lib/types'
import { CatalogueEditor, EMPTY_CATALOGUE } from './CatalogueEditor'
import type { TenantSummary } from './Tenants'

type Role = 'rep' | 'supervisor' | 'admin' | 'head'
interface UserRow {
  id: string
  email: string
  displayName: string
  role: Role
  salesTeam: string | null
  active: boolean
  hasSso: boolean
  hasPassword: boolean
}

const ROLE_TONE: Record<Role, 'neutral' | 'info' | 'warn' | 'pos'> = { rep: 'neutral', supervisor: 'info', head: 'pos', admin: 'warn' }
const teamless = (r: Role) => r === 'admin' || r === 'head'

export function TenantDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [t, setT] = useState<TenantSummary | null>(null)
  const [users, setUsers] = useState<UserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [settings, setSettings] = useState({ name: '', slug: '', currency: '', locale: '' })
  const [form, setForm] = useState({ email: '', displayName: '', role: 'rep' as Role, salesTeam: '', password: '' })
  const [editing, setEditing] = useState<UserRow | null>(null)
  const [edit, setEdit] = useState({ displayName: '', role: 'rep' as Role, salesTeam: '', password: '' })
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [catalogue, setCatalogue] = useState<Catalogue>(EMPTY_CATALOGUE)
  const [catalogueDirty, setCatalogueDirty] = useState(false)

  const reload = useCallback(async () => {
    if (!id) return
    try {
      const [tt, uu] = await Promise.all([api.get<TenantSummary>(`/api/platform/tenants/${id}`), api.get<UserRow[]>(`/api/platform/tenants/${id}/users`)])
      setT(tt)
      setUsers(uu)
      setSettings({ name: tt.name, slug: tt.slug, currency: tt.currency, locale: tt.locale })
      setCatalogue(tt.catalogue ?? EMPTY_CATALOGUE)
      setCatalogueDirty(false)
    } catch (e) {
      setError(errorMessage(e))
    }
  }, [id])
  useEffect(() => {
    reload()
  }, [reload])

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fn()
      await reload()
      if (done) setNotice(done)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (error && !t) return <ErrorBox>{error}</ErrorBox>
  if (!t) return <div className="py-8 text-center text-xs text-slate-500">Loading…</div>
  const settingsDirty = settings.name !== t.name || settings.slug !== t.slug || settings.currency !== t.currency || settings.locale !== t.locale

  return (
    <div className="space-y-4">
      <ScreenHeading
        title={
          <span className="flex items-center gap-2">
            {t.name} {t.active ? <Tag tone="pos">active</Tag> : <Tag tone="neg">inactive</Tag>}
          </span>
        }
        subtitle={`handle ${t.slug} · ${t.currency} · ${t.locale} · created ${dateTime(t.createdAt)}`}
        right={
          <button type="button" className="btn" onClick={() => navigate('/platform/tenants')}>
            All tenants
          </button>
        }
        stats={[
          { label: 'Users', value: t.users, foot: `${users?.filter((u) => u.active).length ?? 0} active` },
          { label: 'Quotes', value: t.quotes },
          { label: 'Active model', value: t.activeModel ? t.activeModel.id.slice(0, 8) : '–', foot: t.activeModel ? t.activeModel.note || '(no note)' : 'the tenant admin imports one', tone: t.activeModel ? undefined : 'warn' },
          { label: 'Status', value: t.active ? 'Active' : 'Inactive', foot: t.active ? 'users can sign in' : 'all sessions refused', tone: t.active ? 'pos' : 'neg' },
        ]}
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      {notice && <InfoBox>{notice}</InfoBox>}

      <Panel
        title="Driver catalogue"
        subtitle={
          t.catalogue
            ? `${t.catalogue.drivers.length} drivers. Drivers declared by existing model versions cannot be removed; new ones can be added and reach the model with the next version.`
            : 'No catalogue yet. Define the drivers this tenant prices with, then scaffold the first model version.'
        }
        right={
          <div className="flex items-center gap-2">
            {!t.catalogue && (
              <button
                type="button"
                className="btn !py-0.5 text-xs"
                disabled={busy}
                onClick={async () => {
                  const tt = await api.get<{ id: string; catalogue: Catalogue }[]>('/api/platform/catalogue-templates')
                  const std = tt.find((x) => x.id === 'standard') ?? tt[0]
                  if (std) {
                    setCatalogue(JSON.parse(JSON.stringify(std.catalogue)) as Catalogue)
                    setCatalogueDirty(true)
                  }
                }}
              >
                Load standard drivers
              </button>
            )}
            <button type="button" className="btn btn-primary !py-0.5 text-xs" disabled={busy || !catalogueDirty || catalogue.drivers.length === 0} onClick={() => run(() => api.patch(`/api/platform/tenants/${id}`, { catalogue }), 'Catalogue saved.')}>
              Save catalogue
            </button>
            <button
              type="button"
              className="btn !py-0.5 text-xs"
              disabled={busy || !t.catalogue || catalogueDirty}
              title={t.activeModel ? 'Creates a zero-weight draft beside the active version' : 'Creates the first model version'}
              onClick={() => run(() => api.post(`/api/platform/tenants/${id}/scaffold-model`, { publish: !t.activeModel }), t.activeModel ? 'Zero-weight draft created.' : 'First model version published with every weight at zero; the admin can now enter weights.')}
            >
              {t.activeModel ? 'Scaffold a zero-weight draft' : 'Scaffold and publish first model'}
            </button>
          </div>
        }
      >
        <CatalogueEditor
          value={catalogue}
          onChange={(c) => {
            setCatalogue(c)
            setCatalogueDirty(true)
          }}
        />
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Settings" className="lg:col-span-1">
          <div className="space-y-3">
            <Field label="Name"><input className="field" value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} /></Field>
            <Field label="Handle"><input className="field font-mono" value={settings.slug} onChange={(e) => setSettings({ ...settings, slug: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Currency"><input className="field font-mono uppercase" maxLength={3} value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} /></Field>
              <Field label="Locale"><input className="field font-mono" value={settings.locale} onChange={(e) => setSettings({ ...settings, locale: e.target.value })} /></Field>
            </div>
            <button type="button" className="btn btn-primary w-full" disabled={busy || !settingsDirty} onClick={() => run(() => api.patch(`/api/platform/tenants/${id}`, settings), 'Settings saved.')}>
              Save settings
            </button>
            <div className="border-t border-slate-800 pt-3">
              {t.active ? (
                <>
                  <WarnBox>Deactivating refuses every session of this tenant at the next request. Data is kept.</WarnBox>
                  <button type="button" className="btn btn-danger mt-2 w-full" disabled={busy} onClick={() => run(() => api.patch(`/api/platform/tenants/${id}`, { active: false }), 'Tenant deactivated.')}>
                    Deactivate tenant
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-pos w-full" disabled={busy} onClick={() => run(() => api.patch(`/api/platform/tenants/${id}`, { active: true }), 'Tenant reactivated.')}>
                  Reactivate tenant
                </button>
              )}
            </div>
          </div>
        </Panel>

        <div className="space-y-4 lg:col-span-2">
          <Panel title="Users" subtitle="One role per user. Sales reps and sales directors belong to a sales team; one sales director per team; the general manager and admin cover all teams.">
            <DataTable
              rows={users ?? []}
              rowKey={(r) => r.id}
              onRowClick={(r) => {
                setEditing(r)
                setEdit({ displayName: r.displayName, role: r.role, salesTeam: r.salesTeam ?? '', password: '' })
              }}
              empty="No users yet. Create the first admin below."
              columns={[
                { key: 'n', header: 'Name', render: (r) => <span className={r.active ? 'text-slate-200' : 'text-slate-500 line-through'}>{r.displayName}</span> },
                { key: 'e', header: 'Email', render: (r) => <span className="text-slate-400">{r.email}</span> },
                { key: 'r', header: 'Role', render: (r) => <Tag tone={ROLE_TONE[r.role]}>{r.role}</Tag> },
                { key: 't', header: 'Team', render: (r) => r.salesTeam ?? <span className="text-slate-600">all</span> },
                { key: 'a', header: 'Sign-in', render: (r) => <span className="text-xs text-slate-400">{[r.hasSso && 'SSO', r.hasPassword && 'password'].filter(Boolean).join(' · ') || 'not yet'}</span> },
                { key: 's', header: '', render: (r) => (r.active ? null : <Tag tone="neg">inactive</Tag>) },
              ]}
            />
          </Panel>

          {editing && (
            <Panel title={`Edit ${editing.email}`} right={<button type="button" className="btn !py-0.5 text-xs" onClick={() => setEditing(null)}>Close</button>}>
              <div className="grid gap-3 md:grid-cols-4">
                <Field label="Display name"><input className="field" value={edit.displayName} onChange={(e) => setEdit({ ...edit, displayName: e.target.value })} /></Field>
                <Field label="Role">
                  <select className="field" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value as Role })}>
                    {(['rep', 'supervisor', 'head', 'admin'] as Role[]).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                </Field>
                <Field label="Sales team"><input className="field" disabled={teamless(edit.role)} value={teamless(edit.role) ? '' : edit.salesTeam} onChange={(e) => setEdit({ ...edit, salesTeam: e.target.value })} /></Field>
                <Field label="New password (optional)"><input className="field" type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} /></Field>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || (!teamless(edit.role) && !edit.salesTeam)}
                  onClick={() =>
                    run(
                      () =>
                        api.patch(`/api/platform/tenants/${id}/users/${editing.id}`, {
                          displayName: edit.displayName,
                          role: edit.role,
                          salesTeam: teamless(edit.role) ? null : edit.salesTeam,
                          ...(edit.password ? { password: edit.password } : {}),
                        }),
                      'User saved.',
                    ).then(() => setEditing(null))
                  }
                >
                  Save
                </button>
                <button type="button" className={editing.active ? 'btn btn-danger' : 'btn btn-pos'} disabled={busy} onClick={() => run(() => api.patch(`/api/platform/tenants/${id}/users/${editing.id}`, { active: !editing.active }), editing.active ? 'User deactivated.' : 'User reactivated.').then(() => setEditing(null))}>
                  {editing.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </Panel>
          )}

          <Panel title="Add a user" subtitle="SSO users sign in with their company account once provisioned by email. A password is only for local development.">
            <div className="grid gap-3 md:grid-cols-5">
              <Field label="Email"><input className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Display name"><input className="field" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></Field>
              <Field label="Role">
                <select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                  {(['rep', 'supervisor', 'head', 'admin'] as Role[]).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
              </Field>
              <Field label="Sales team"><input className="field" disabled={teamless(form.role)} value={teamless(form.role) ? '' : form.salesTeam} onChange={(e) => setForm({ ...form, salesTeam: e.target.value })} placeholder="AT-North" /></Field>
              <Field label="Password (optional)"><input className="field" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !form.email || !form.displayName || (!teamless(form.role) && !form.salesTeam)}
                onClick={() =>
                  run(
                    () =>
                      api.post(`/api/platform/tenants/${id}/users`, {
                        email: form.email,
                        displayName: form.displayName,
                        role: form.role,
                        salesTeam: teamless(form.role) ? null : form.salesTeam,
                        ...(form.password ? { password: form.password } : {}),
                      }),
                    'User created.',
                  ).then(() => setForm({ email: '', displayName: '', role: 'rep', salesTeam: '', password: '' }))
                }
              >
                Create user
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}
